/**
 * Per-chapter Story Craft feedback (aligned with web `ChapterStoryCraftDTO` / legacy desktop store).
 */

import {
  CHAPTER_VARIATION_DIALOGUE_RULE,
  CHAPTER_VARIATION_FORMATTING_RULE,
} from "./chapterVariationIntent";

export type StoryCraftScoreNotes = { score: number; notes: string };

export type StoryCraftAssessment = {
  plotProgression: StoryCraftScoreNotes;
  characterDevelopment: StoryCraftScoreNotes;
  themeReinforcement: StoryCraftScoreNotes;
  pacing: StoryCraftScoreNotes;
  conflictTension: StoryCraftScoreNotes;
  hookEnding: StoryCraftScoreNotes;
  overallNotes: string;
};

export type StoryCraftChecklistItem = {
  id: string;
  category: string;
  suggestion: string;
  isCompleted: boolean;
  completedAt?: string;
  addedAt: string;
};

export type StoryPromiseRow = {
  id: string;
  type: string;
  description: string;
  context: string;
  chapterId: string;
  chapterTitle: string;
};

export type PromiseKeptRow = {
  promiseId: string;
  promiseDescription: string;
  howKept: string;
  chapterWherePromised: string;
  chapterTitleWherePromised: string;
};

/** Narrative seed planted in this chapter that pays off in a later chapter. */
export type StorySeedRow = {
  id: string;
  description: string;
  context: string;
  payoffChapterId: string;
  payoffChapterTitle: string;
  payoffDescription: string;
};

export type DesktopStoryCraftFeedback = {
  chapterId: string;
  chapterTitle: string;
  assessment: StoryCraftAssessment;
  checklist: StoryCraftChecklistItem[];
  summary: string;
  promisesMade: StoryPromiseRow[];
  promisesKept: PromiseKeptRow[];
  seeds: StorySeedRow[];
  generatedAt: string;
  lastUpdated: string;
};

/** @deprecated v2-only placeholder shape before real AI feedback */
export type LegacyStoryCraftSection = {
  id: string;
  title: string;
  insight: string;
  action: string;
};

export type LegacyAssistantStoryCraftReport = {
  generatedAt: string;
  sections: LegacyStoryCraftSection[];
};

export function generateStoryCraftId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createStoryCraftChecklistItem(
  partial?: Partial<Pick<StoryCraftChecklistItem, "category" | "suggestion">>
): StoryCraftChecklistItem {
  const now = new Date().toISOString();
  return {
    id: generateStoryCraftId("check"),
    category: partial?.category?.trim() || "general",
    suggestion: partial?.suggestion?.trim() || "",
    isCompleted: false,
    addedAt: now,
  };
}

function emptyAssessment(defaultScore = 0): StoryCraftAssessment {
  const blank = (): StoryCraftScoreNotes => ({ score: defaultScore, notes: "" });
  return {
    plotProgression: blank(),
    characterDevelopment: blank(),
    themeReinforcement: blank(),
    pacing: blank(),
    conflictTension: blank(),
    hookEnding: blank(),
    overallNotes: "",
  };
}

function isScoreBlock(v: unknown): v is StoryCraftScoreNotes {
  if (!v || typeof v !== "object") return false;
  const o = v as { score?: unknown; notes?: unknown };
  return typeof o.score === "number" && typeof o.notes === "string";
}

function coerceAssessment(raw: unknown, defaultScore = 0): StoryCraftAssessment {
  if (!raw || typeof raw !== "object") return emptyAssessment(defaultScore);
  const a = raw as Record<string, unknown>;
  const keys = [
    "plotProgression",
    "characterDevelopment",
    "themeReinforcement",
    "pacing",
    "conflictTension",
    "hookEnding",
  ] as const;
  const out = emptyAssessment(defaultScore);
  for (const k of keys) {
    if (isScoreBlock(a[k])) {
      const s = a[k];
      out[k] = {
        score: Math.min(5, Math.max(1, Math.round(s.score))),
        notes: s.notes,
      };
    }
  }
  out.overallNotes = typeof a.overallNotes === "string" ? a.overallNotes : "";
  return out;
}

/** Public helpers for editor scorecard UI (same shape as AI StoryCraft). */
export function emptyStoryCraftAssessment(defaultScore = 3): StoryCraftAssessment {
  return emptyAssessment(defaultScore);
}

export function coerceStoryCraftAssessment(raw: unknown): StoryCraftAssessment {
  return coerceAssessment(raw, 3);
}

export function normalizeStoryCraftFromApiJson(
  parsed: Record<string, unknown>,
  chapterId: string,
  chapterTitle: string,
  nowIso: string
): DesktopStoryCraftFeedback {
  const checklistRaw = Array.isArray(parsed.checklist) ? parsed.checklist : [];
  const checklist: StoryCraftChecklistItem[] = checklistRaw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      id: generateStoryCraftId("check"),
      category: typeof item.category === "string" ? item.category : "general",
      suggestion: typeof item.suggestion === "string" ? item.suggestion : "",
      isCompleted: false,
      addedAt: nowIso,
    }))
    .filter((item) => item.suggestion.trim().length > 0);

  const promisesMadeRaw = Array.isArray(parsed.promisesMade) ? parsed.promisesMade : [];
  const promisesMade: StoryPromiseRow[] = promisesMadeRaw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      id: generateStoryCraftId("prom"),
      type: typeof item.type === "string" ? item.type : "setup",
      description: typeof item.description === "string" ? item.description : "",
      context: typeof item.context === "string" ? item.context : "",
      chapterId,
      chapterTitle,
    }))
    .filter((row) => row.description.trim().length > 0);

  const promisesKeptRaw = Array.isArray(parsed.promisesKept) ? parsed.promisesKept : [];
  const promisesKept: PromiseKeptRow[] = promisesKeptRaw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      promiseId: typeof item.promiseId === "string" ? item.promiseId : "",
      promiseDescription: typeof item.promiseDescription === "string" ? item.promiseDescription : "",
      howKept: typeof item.howKept === "string" ? item.howKept : "",
      chapterWherePromised: typeof item.chapterWherePromised === "string" ? item.chapterWherePromised : "",
      chapterTitleWherePromised:
        typeof item.chapterTitleWherePromised === "string" ? item.chapterTitleWherePromised : "",
    }))
    .filter((row) => row.promiseId.trim().length > 0 || row.promiseDescription.trim().length > 0);

  const seedsRaw = Array.isArray(parsed.seeds) ? parsed.seeds : [];
  const seeds: StorySeedRow[] = seedsRaw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      id: generateStoryCraftId("seed"),
      description: typeof item.description === "string" ? item.description : "",
      context: typeof item.context === "string" ? item.context : "",
      payoffChapterId: typeof item.payoffChapterId === "string" ? item.payoffChapterId : "",
      payoffChapterTitle: typeof item.payoffChapterTitle === "string" ? item.payoffChapterTitle : "",
      payoffDescription: typeof item.payoffDescription === "string" ? item.payoffDescription : "",
    }))
    .filter((row) => row.description.trim().length > 0 && row.payoffDescription.trim().length > 0);

  return {
    chapterId,
    chapterTitle,
    assessment: coerceAssessment(parsed.assessment),
    checklist,
    summary: typeof parsed.summary === "string" ? parsed.summary : "",
    promisesMade,
    promisesKept,
    seeds,
    generatedAt: nowIso,
    lastUpdated: nowIso,
  };
}

export function storyCraftAnalyzingPlaceholder(chapterId: string, chapterTitle: string): DesktopStoryCraftFeedback {
  const now = new Date().toISOString();
  return {
    chapterId,
    chapterTitle,
    assessment: emptyAssessment(),
    checklist: [],
    summary: "Analyzing…",
    promisesMade: [],
    promisesKept: [],
    seeds: [],
    generatedAt: now,
    lastUpdated: now,
  };
}

export function storyCraftFailedPlaceholder(chapterId: string, chapterTitle: string): DesktopStoryCraftFeedback {
  const now = new Date().toISOString();
  return {
    chapterId,
    chapterTitle,
    assessment: emptyAssessment(),
    checklist: [],
    summary:
      "Analysis failed. Configure OPENAI_API_KEY or ANTHROPIC_API_KEY in .env, then run Story Craft again.",
    promisesMade: [],
    promisesKept: [],
    seeds: [],
    generatedAt: now,
    lastUpdated: now,
  };
}

export function storyCraftErrorFeedback(
  chapterId: string,
  chapterTitle: string,
  errorMessage: string
): DesktopStoryCraftFeedback {
  const base = storyCraftFailedPlaceholder(chapterId, chapterTitle);
  return {
    ...base,
    summary: `Analysis failed: ${errorMessage.trim() || "Unknown error"}. Fix configuration and run Story Craft again.`,
  };
}

export function legacySectionsToFeedback(
  legacy: LegacyAssistantStoryCraftReport,
  chapterId: string,
  chapterTitle: string
): DesktopStoryCraftFeedback {
  const now = legacy.generatedAt || new Date().toISOString();
  const checklist: StoryCraftChecklistItem[] = legacy.sections.map((section) => ({
    id: generateStoryCraftId("check"),
    category: "general",
    suggestion: `${section.title}: ${section.insight} — Action: ${section.action}`,
    isCompleted: false,
    addedAt: now,
  }));
  return {
    chapterId,
    chapterTitle,
    assessment: emptyAssessment(),
    checklist,
    summary: "Earlier Story Craft run (legacy format). Re-run Story Craft for full scores and promises.",
    promisesMade: [],
    promisesKept: [],
    seeds: [],
    generatedAt: now,
    lastUpdated: now,
  };
}

export function formatStoryCraftFeedbackAsDocument(fb: DesktopStoryCraftFeedback): string {
  const lines: string[] = [];
  lines.push(`# Story Craft — ${fb.chapterTitle}`);
  lines.push("");
  lines.push(`*Generated: ${new Date(fb.generatedAt).toLocaleString()}*`);
  lines.push("");
  lines.push("## Summary");
  lines.push(fb.summary.trim() || "(No summary.)");
  lines.push("");
  lines.push("## Scores (1–5)");
  const a = fb.assessment;
  const dims: [string, StoryCraftScoreNotes][] = [
    ["Plot progression", a.plotProgression],
    ["Character development", a.characterDevelopment],
    ["Theme reinforcement", a.themeReinforcement],
    ["Pacing", a.pacing],
    ["Conflict / tension", a.conflictTension],
    ["Hook / ending", a.hookEnding],
  ];
  for (const [label, block] of dims) {
    lines.push(`- ${label}: ${block.score} — ${block.notes.trim() || "—"}`);
  }
  if (a.overallNotes.trim()) {
    lines.push("");
    lines.push("### Overall notes");
    lines.push(a.overallNotes.trim());
  }
  lines.push("");
  lines.push("## Improvement checklist");
  if (fb.checklist.length === 0) {
    lines.push("(None.)");
  } else {
    for (const item of fb.checklist) {
      lines.push(`- [${item.category}] ${item.suggestion}`);
    }
  }
  lines.push("");
  lines.push("## Promises made (this chapter)");
  if (fb.promisesMade.length === 0) {
    lines.push("(None identified.)");
  } else {
    for (const p of fb.promisesMade) {
      lines.push(`- (${p.type}) ${p.description}`);
      if (p.context.trim()) lines.push(`  Context: ${p.context}`);
    }
  }
  lines.push("");
  lines.push("## Promises kept (from earlier chapters)");
  if (fb.promisesKept.length === 0) {
    lines.push("(None identified.)");
  } else {
    for (const p of fb.promisesKept) {
      lines.push(`- ${p.promiseDescription}`);
      lines.push(`  How kept: ${p.howKept}`);
      if (p.chapterTitleWherePromised) {
        lines.push(`  Promised in: ${p.chapterTitleWherePromised}`);
      }
    }
  }
  lines.push("");
  lines.push("## Seeds (payoffs in later chapters)");
  if (fb.seeds.length === 0) {
    lines.push("(None identified.)");
  } else {
    for (const s of fb.seeds) {
      lines.push(`- ${s.description}`);
      if (s.context.trim()) lines.push(`  Context: ${s.context}`);
      lines.push(`  Payoff: ${s.payoffDescription}`);
      if (s.payoffChapterTitle) lines.push(`  Pays off in: ${s.payoffChapterTitle}`);
    }
  }
  return lines.join("\n");
}

export function averageStoryCraftScore(fb: DesktopStoryCraftFeedback): number | null {
  const a = fb.assessment;
  const blocks = [
    a.plotProgression,
    a.characterDevelopment,
    a.themeReinforcement,
    a.pacing,
    a.conflictTension,
    a.hookEnding,
  ];
  const scores = blocks.map((b) => b.score).filter((s) => typeof s === "number" && s > 0);
  if (scores.length === 0) return null;
  return scores.reduce((x, y) => x + y, 0) / scores.length;
}

function isLegacyReport(v: Record<string, unknown>): v is LegacyAssistantStoryCraftReport {
  return Array.isArray(v.sections) && typeof v.generatedAt === "string" && !("assessment" in v);
}

function cloneChecklist(raw: unknown): StoryCraftChecklistItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      id: typeof item.id === "string" ? item.id : generateStoryCraftId("check"),
      category: typeof item.category === "string" ? item.category : "general",
      suggestion: typeof item.suggestion === "string" ? item.suggestion : "",
      isCompleted: Boolean(item.isCompleted),
      completedAt: typeof item.completedAt === "string" ? item.completedAt : undefined,
      addedAt: typeof item.addedAt === "string" ? item.addedAt : new Date().toISOString(),
    }))
    .filter((item) => item.suggestion.trim().length > 0);
}

function clonePromisesMade(raw: unknown): StoryPromiseRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      id: typeof item.id === "string" ? item.id : generateStoryCraftId("prom"),
      type: typeof item.type === "string" ? item.type : "",
      description: typeof item.description === "string" ? item.description : "",
      context: typeof item.context === "string" ? item.context : "",
      chapterId: typeof item.chapterId === "string" ? item.chapterId : "",
      chapterTitle: typeof item.chapterTitle === "string" ? item.chapterTitle : "",
    }))
    .filter((row) => row.description.trim().length > 0);
}

function clonePromisesKept(raw: unknown): PromiseKeptRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      promiseId: typeof item.promiseId === "string" ? item.promiseId : "",
      promiseDescription: typeof item.promiseDescription === "string" ? item.promiseDescription : "",
      howKept: typeof item.howKept === "string" ? item.howKept : "",
      chapterWherePromised: typeof item.chapterWherePromised === "string" ? item.chapterWherePromised : "",
      chapterTitleWherePromised:
        typeof item.chapterTitleWherePromised === "string" ? item.chapterTitleWherePromised : "",
    }));
}

function cloneSeeds(raw: unknown): StorySeedRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      id: typeof item.id === "string" ? item.id : generateStoryCraftId("seed"),
      description: typeof item.description === "string" ? item.description : "",
      context: typeof item.context === "string" ? item.context : "",
      payoffChapterId: typeof item.payoffChapterId === "string" ? item.payoffChapterId : "",
      payoffChapterTitle: typeof item.payoffChapterTitle === "string" ? item.payoffChapterTitle : "",
      payoffDescription: typeof item.payoffDescription === "string" ? item.payoffDescription : "",
    }))
    .filter((row) => row.description.trim().length > 0);
}

/** Checklist rows to drive a chapter variation (incomplete items by default). */
export function storyCraftChecklistItemsForVariation(
  feedback: DesktopStoryCraftFeedback,
  options?: { onlyIncomplete?: boolean }
): StoryCraftChecklistItem[] {
  const onlyIncomplete = options?.onlyIncomplete ?? true;
  return feedback.checklist.filter((item) => (onlyIncomplete ? !item.isCompleted : true));
}

/** Build variation-generation instructions from Story Craft's improvement checklist. */
export function buildStoryCraftChecklistVariationInstructions(
  feedback: DesktopStoryCraftFeedback,
  options?: { onlyIncomplete?: boolean }
): string | null {
  const items = storyCraftChecklistItemsForVariation(feedback, options);
  if (!items.length) return null;

  const summary = feedback.summary?.trim() ?? "";
  const lines = [
    "Rewrite this chapter to address the Story Craft improvement checklist below.",
    "Preserve plot, POV, character names, timeline, and continuity unless a checklist item explicitly requires a story change.",
    "Apply each item as a targeted revision — do not rewrite unrelated passages.",
    "Do not invent new characters, places, events, objects, or backstory. Checklist items are editorial guidance — address them only with facts already in this chapter or established elsewhere in the manuscript.",
    "If a checklist item cannot be satisfied without adding new story content, implement its spirit within existing material or leave that aspect unchanged.",
    CHAPTER_VARIATION_DIALOGUE_RULE,
    CHAPTER_VARIATION_FORMATTING_RULE,
    "Output the complete chapter manuscript (prose only).",
  ];

  if (summary && summary !== "Analyzing…" && !summary.startsWith("Analysis failed")) {
    lines.push("", "Story Craft summary (context):", summary);
  }

  const overallNotes = feedback.assessment?.overallNotes?.trim();
  if (overallNotes) {
    lines.push("", "Overall assessment notes:", overallNotes);
  }

  lines.push("", "Improvement checklist:");
  for (const item of items) {
    lines.push(`- [${item.category}] ${item.suggestion}`);
  }

  return lines.join("\n");
}

/** Restore Story Craft from `desktopAssistant.storyCraftByChapter` JSON (supports legacy `sections` shape). */
export function loadPersistedStoryCraftFeedback(
  chapterId: string,
  chapterTitle: string,
  value: unknown
): DesktopStoryCraftFeedback | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (isLegacyReport(v)) {
    return legacySectionsToFeedback(v, chapterId, chapterTitle);
  }
  if (!v.assessment || typeof v.summary !== "string") return null;
  const generatedAt = typeof v.generatedAt === "string" ? v.generatedAt : new Date().toISOString();
  const lastUpdated = typeof v.lastUpdated === "string" ? v.lastUpdated : generatedAt;
  return {
    chapterId: typeof v.chapterId === "string" ? v.chapterId : chapterId,
    chapterTitle: typeof v.chapterTitle === "string" ? v.chapterTitle : chapterTitle,
    assessment: coerceAssessment(v.assessment),
    checklist: cloneChecklist(v.checklist),
    summary: v.summary,
    promisesMade: clonePromisesMade(v.promisesMade),
    promisesKept: clonePromisesKept(v.promisesKept),
    seeds: cloneSeeds(v.seeds),
    generatedAt,
    lastUpdated,
  };
}

/** Text for `.sbk` chapter export — prefers `desktopAssistant.storyCraftByChapter`, falls back to legacy tabData. */
export function storyCraftExportTextForChapter(
  bookMetadata: Record<string, unknown> | undefined,
  chapterId: string,
  chapterTitle: string,
  legacyTabDataText?: string
): string {
  const assistant = bookMetadata?.desktopAssistant;
  if (assistant && typeof assistant === "object") {
    const byChapter = (assistant as { storyCraftByChapter?: unknown }).storyCraftByChapter;
    if (byChapter && typeof byChapter === "object") {
      const loaded = loadPersistedStoryCraftFeedback(
        chapterId,
        chapterTitle,
        (byChapter as Record<string, unknown>)[chapterId]
      );
      if (loaded) return formatStoryCraftFeedbackAsDocument(loaded);
    }
  }
  return legacyTabDataText?.trim() ?? "";
}
